import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import { ExternalLink, Undo2 } from "lucide-react"
import {
  contentControllerGetContentSource,
  ContentItemCreationStatus,
  type ContentItem,
} from "@/shared/api/generated"
import { useTextChunkWords, type TextChunkWord } from "@/entities/text-viewer"
import { activePlayer } from "@/entities/audio-engine"
import { processSocket } from "@/entities/process-socket/store"
import { useAutoTranslation } from "@/entities"
import {
  LANG_KEYS,
  QUERY_KEYS,
  ROUTER_PATHS,
  SOCKET_RESPONSES,
} from "@/shared/constants"
import { Spinner } from "@/components/ui/spinner"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import styles from "./styles.module.scss"

interface TextViewerProps {
  itemData: ContentItem
}

interface WordRange {
  word: TextChunkWord
  start: number
  end: number
}

/**
 * Map words onto the source text. Server-provided char offsets are exact
 * UTF-16 offsets; when one is missing / out of order (a token the aligner
 * could not resolve) fall back to a forward text search so later words keep
 * lining up. Mirrors the mobile text view.
 */
const resolveWordRanges = (
  text: string,
  words: TextChunkWord[]
): WordRange[] => {
  let searchFrom = 0
  const ranges: WordRange[] = []
  for (const word of words) {
    let start = word.char_start ?? -1
    let end = word.char_end ?? -1
    if (start < searchFrom || end <= start || end > text.length) {
      start = text.indexOf(word.text, searchFrom)
      end = start < 0 ? -1 : start + word.text.length
    }
    if (start < 0 || end <= start) continue
    searchFrom = end
    ranges.push({ word, start, end })
  }
  return ranges
}

/** Last timed word whose start_ts <= time (held until the next one starts). */
const findActiveIndex = (
  timed: { start: number; index: number }[],
  time: number
): number => {
  if (!timed.length || time < timed[0].start) return -1
  let left = 0
  let right = timed.length - 1
  let found = -1
  while (left <= right) {
    const mid = Math.floor((left + right) / 2)
    if (timed[mid].start <= time) {
      found = mid
      left = mid + 1
    } else {
      right = mid - 1
    }
  }
  return found === -1 ? -1 : timed[found].index
}

const SEEK_THRESHOLD = 2

const hostOf = (url: string) => {
  try {
    return new URL(url).host
  } catch {
    return url
  }
}

export const TextViewer = ({ itemData }: TextViewerProps) => {
  const { t } = useAutoTranslation()
  const navigate = useNavigate()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [autoScroll, setAutoScroll] = useState(true)
  const autoScrollRef = useRef(true)
  autoScrollRef.current = autoScroll

  const isProcessing =
    itemData.creationStatus ===
    ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING

  const { data: source } = useQuery({
    queryKey: QUERY_KEYS.CONTENT_SOURCE(itemData.id),
    queryFn: () => contentControllerGetContentSource(itemData.id),
    staleTime: Infinity,
  })

  const { words, invalidate } = useTextChunkWords(
    itemData.id,
    isProcessing,
    itemData.combinedDataAbsoluteUrls
  )

  // New chunks / deferred words land while listening to a still-processing
  // note — same two events the markdown viewer listens to.
  useEffect(() => {
    if (!isProcessing) return
    const handler = () => invalidate()
    processSocket.on(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
    processSocket.on(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    return () => {
      processSocket.off(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
      processSocket.off(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    }
  }, [isProcessing, invalidate])

  // ─── Text → segments ────────────────────────────────────

  const text = source?.text ?? ""

  const ranges = useMemo(() => resolveWordRanges(text, words), [text, words])

  /** Sorted (start_ts, range index) for the karaoke lookup */
  const timed = useMemo(
    () =>
      ranges
        .map((range, index) => ({ start: range.word.start_ts, index }))
        .filter((entry): entry is { start: number; index: number } =>
          typeof entry.start === "number"
        )
        .sort((a, b) => a.start - b.start),
    [ranges]
  )

  const segments = useMemo(() => {
    const out: (string | WordRange & { index: number })[] = []
    let cursor = 0
    ranges.forEach((range, index) => {
      if (range.start > cursor) out.push(text.slice(cursor, range.start))
      out.push({ ...range, index })
      cursor = range.end
    })
    if (cursor < text.length) out.push(text.slice(cursor))
    return out
  }, [text, ranges])

  // ─── Karaoke highlight (rAF, imperative class toggle) ───

  const activeElRef = useRef<HTMLElement | null>(null)
  const lastPositionRef = useRef(0)
  const rangesRef = useRef(ranges)
  const timedRef = useRef(timed)
  rangesRef.current = ranges
  timedRef.current = timed

  const applyHighlight = useCallback((index: number, instant: boolean) => {
    const element =
      index >= 0 ? document.getElementById(`tw${index}`) : null
    if (element === activeElRef.current) return
    activeElRef.current?.classList.remove("active")
    activeElRef.current = element
    if (!element) return
    element.classList.add("active")

    if (!autoScrollRef.current || !scrollContainerRef.current) return
    const container = scrollContainerRef.current
    const viewportHeight = container.clientHeight
    const containerRect = container.getBoundingClientRect()
    const wordRect = element.getBoundingClientRect()
    const top = wordRect.top - containerRect.top
    const bottom = wordRect.bottom - containerRect.top
    const offScreen = bottom < 0 || top > viewportHeight
    const nearEdge = top < viewportHeight * 0.2 || bottom > viewportHeight * 0.8
    if (instant || offScreen || nearEdge) {
      container.scrollTo({
        top: top + container.scrollTop - viewportHeight * 0.3,
        behavior: instant || offScreen ? "instant" : "smooth",
      })
    }
  }, [])

  useEffect(() => {
    let rafId: number | null = null
    const check = () => {
      rafId = requestAnimationFrame(check)
      if (activePlayer.id !== itemData.id) return
      const pos = activePlayer.currentPosition
      const jumped = Math.abs(pos - lastPositionRef.current) > SEEK_THRESHOLD
      lastPositionRef.current = pos
      if (pos <= 0) {
        applyHighlight(-1, false)
        return
      }
      applyHighlight(findActiveIndex(timedRef.current, pos), jumped)
    }
    rafId = requestAnimationFrame(check)
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId)
      activeElRef.current?.classList.remove("active")
      activeElRef.current = null
    }
  }, [itemData.id, applyHighlight])

  const handleSetAutoScroll = (value: boolean) => {
    setAutoScroll(value)
    autoScrollRef.current = value
    if (value && activeElRef.current) {
      const index = Number(activeElRef.current.id.slice(2))
      activeElRef.current = null
      applyHighlight(index, true)
    }
  }

  // ─── Audio ──────────────────────────────────────────────

  const hasStartedAudio = useRef(false)
  useEffect(() => {
    if (hasStartedAudio.current) return
    hasStartedAudio.current = true
    if (activePlayer.id === itemData.id && activePlayer.isActive) return
    if (navigator.userActivation?.hasBeenActive) {
      activePlayer.play(itemData)
    } else {
      activePlayer.load(itemData)
    }
  }, [itemData])

  // Click any timed word → seek there (delegated).
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const span = (e.target as HTMLElement).closest?.(".tts-word")
    if (!span) return
    const match = /^tw(\d+)$/.exec(span.id)
    if (!match) return
    const range = rangesRef.current[Number(match[1])]
    if (!range || typeof range.word.start_ts !== "number") return
    activePlayer.seekToAndPlay(range.word.start_ts)
  }, [])

  if (!source) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="bg-background flex items-center justify-between gap-2 border-b px-2"
        style={{ minHeight: "56px" }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(ROUTER_PATHS.APP_LIBRARY)}
        >
          <Undo2 className="h-5 w-5" />
        </Button>

        <div className="flex min-w-0 flex-1 items-center justify-center gap-3">
          {source.sourceUrl && (
            <span className={styles.source}>
              <a
                href={source.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                title={source.sourceUrl}
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                <span>{source.sourceTitle || hostOf(source.sourceUrl)}</span>
              </a>
            </span>
          )}
        </div>

        <div className="flex items-center gap-1.5">
          <Checkbox
            id="auto-scroll"
            checked={autoScroll}
            onCheckedChange={checked => handleSetAutoScroll(checked === true)}
          />
          <Label
            htmlFor="auto-scroll"
            className="cursor-pointer text-sm font-medium"
          >
            {t(LANG_KEYS.AUTO_SCROLL)}
          </Label>
        </div>
      </div>

      <div className="flex-1 overflow-auto" ref={scrollContainerRef}>
        <div className="mx-auto w-full max-w-[860px] px-4 py-6 sm:px-6 lg:px-8">
          <div className={styles.textBody} onClick={handleContentClick}>
            {segments.map((segment, i) =>
              typeof segment === "string" ? (
                <span key={`t${i}`}>{segment}</span>
              ) : (
                <span
                  key={`w${segment.index}`}
                  id={`tw${segment.index}`}
                  className={
                    typeof segment.word.start_ts === "number"
                      ? "tts-word ready"
                      : "tts-word"
                  }
                >
                  {text.slice(segment.start, segment.end)}
                </span>
              )
            )}
          </div>
        </div>
        <div className="h-24" />
      </div>
    </div>
  )
}
